import {
  MouseEvent,
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Box, BoxProps, MantineTheme, rem } from '@mantine/core'
import innertext from 'innertext'
import dynamic from 'next/dynamic'

import { callApiNew } from 'api/axios'
import { SpotlightSessionType } from 'api/dataTransform/carePath'
import { PATHS } from 'api/paths'
import { Container } from 'components/atoms/container'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/fixedBottomAdBlock'
import AIChatBot, {
  HandlerProps as AIChatBoHandlerProps,
} from 'components/molecules/aiChatbot'
import { AppFooterBanner } from 'components/molecules/appFooterBanner'
import CarePathDoctorList, {
  HandlerProps as CarePathDoctorListHandlerProps,
} from 'components/molecules/carePathDoctorList'
import { CarePathProductWidgets } from 'components/molecules/carePathProductWidgets'
import {
  ConfirmLeavePageHandlerProps,
  ConfirmLeavePageProps,
} from 'components/molecules/confirmLeavePage/'
import { ConfirmLeavePageWapperProps } from 'components/molecules/confirmLeavePage/wrapper'
import { HandlerProps as IdlePopupHandlerProps } from 'components/molecules/idlePopup'
import { Props as IdlePopupProps } from 'components/molecules/idlePopup'
import { Props as IdlePopupWrapperProps } from 'components/molecules/idlePopup/wrapper'
import { PfizerDisclaimer } from 'components/molecules/pfizerDisclaimer'
import { StickySponsoredBy } from 'components/molecules/sponsoredBy'
import { AppIntroCards } from 'components/organisms/appIntroCards'
import { AppIntroHeroBanner } from 'components/organisms/appIntroHeroBanner'
import { ClinicLocatorProps } from 'components/organisms/clinicLocator/'
import { InfoCardWithSmallIconSection } from 'components/organisms/infoCardWithSmallIconSection'
import { InfoGardientSection } from 'components/organisms/infoGardientSection'
import ScrollingBlocks, {
  HandlerProps as ScrollingBlocksHandlerProps,
} from 'components/organisms/scrollingBlocks'
import { ArticleCardList } from 'components/organisms/serviceConnections/articleCardList'
import { ClickableSingleBanner } from 'components/organisms/serviceConnections/clickableSingleBanner'
import { ContentExpert } from 'components/organisms/serviceConnections/contentExpert'
import { CtaBannerSection } from 'components/organisms/serviceConnections/ctaBannerSection'
import { CustomizedBlock } from 'components/organisms/serviceConnections/customizedBlock'
import { EmbedCommunityWithPosts } from 'components/organisms/serviceConnections/embedCommunityWithPosts'
import { EmbedPost } from 'components/organisms/serviceConnections/embedPost'
import { HealthToolEmbed } from 'components/organisms/serviceConnections/healthToolEmbed'
import { HeroBanner } from 'components/organisms/serviceConnections/heroBanner'
import { CtaBanner } from 'components/organisms/serviceConnections/heroBanner/ctaBanner'
import { CustomBanner } from 'components/organisms/serviceConnections/heroBanner/customLayout'
import { DefaultBanner } from 'components/organisms/serviceConnections/heroBanner/defaultLayout'
import { HERO_BANNER_SESSION_LAYOUT_LISTS } from 'components/organisms/serviceConnections/heroBanner/navigator'
import { BlockItemPropsType } from 'components/organisms/serviceConnections/heroBanner/navigator/blockItem'
import { ElegantUI } from 'components/organisms/serviceConnections/heroBanner/navigator/blockItem/elegant'
import { FlatIcon } from 'components/organisms/serviceConnections/heroBanner/navigator/blockItem/flatIcon'
import { FullBg } from 'components/organisms/serviceConnections/heroBanner/navigator/blockItem/fullBg'
import { ImageIcon } from 'components/organisms/serviceConnections/heroBanner/navigator/blockItem/imageIcon'
import LeadgenWithBanner from 'components/organisms/serviceConnections/leadgenWithBanner'
import { MultiRiskScreenerEmbed } from 'components/organisms/serviceConnections/multiRiskScreenerEmbed'
import { Quotations } from 'components/organisms/serviceConnections/quotations'
import { RiskScreenerEmbed } from 'components/organisms/serviceConnections/riskScreenerEmbed'
import { ScheduleEvent } from 'components/organisms/serviceConnections/scheduleEvent'
import { ShortReelList } from 'components/organisms/serviceConnections/shortReelList'
import {
  StickyNavigatorHandlerProps,
  StickyNavigatorProps,
} from 'components/organisms/serviceConnections/stickyNavigator'
import { StickyNavWrapperProps } from 'components/organisms/serviceConnections/stickyNavigator/wrapper'
import { VideoLibrary } from 'components/organisms/serviceConnections/videoLibrary'
import { MastheadAd } from 'components/pages/article/components/MastheadAd'
import { OutOfPageAd } from 'components/pages/article/components/OutOfPageAd'
import { Props as DynamicSpotlightPageProps } from 'components/pages/dynamicSpotlight'
import {
  BannerProps,
  ClickableBanner,
} from 'components/pages/landingPage/clickableBanner'
import { StyledLandingWrapper } from 'components/pages/landingPage/index.styled'
import { GTMScript } from 'components/templates/appWrappers/GTMScript'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { domainLocales } from 'domainLocales'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useDetectSCpage } from 'hooks/useDetectSCpage'
import { useIsMobile } from 'hooks/useIsMobile'
import { useMastheadAdEnable } from 'hooks/useMastheadAdEnable'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS } from 'interfaces/constants'
import {
  SOYA_AI_CHAT_ID,
  SOYA_HASH_TO_OPEN_LEAD_MODAl,
} from 'interfaces/constants/configs'
import {
  Article,
  ContentBannerSession,
  LOCALE,
  ScrollToItemType,
  SessionType,
  Spotlight,
  SpotlightArticleSession,
  SpotlightClickableBannerSession,
  SpotlightClickableSingleBannerSession,
  SpotlightClinicLocatorSession,
  SpotlightCommunityWithPostEmbedType,
  SpotlightCommunityWithPostsSession,
  SpotlightContentExpertSession,
  SpotlightCTABannerSession,
  SpotlightCustomizeBlockSession,
  SpotlightEcommerceProductsSession,
  SpotlightEmbeddingDoctorsSession,
  SpotlightHealthToolListSession,
  SpotlightHealthToolSession,
  SpotlightHeroBannerSession,
  SpotlightLeadgenSession,
  SpotlightMultiRiskScreenerSession,
  SpotlightQuotationsSession,
  SpotlightRiskScreenerSession,
  SpotlightScheduleEventSession,
  SpotlightVideoSession,
  SpotlightVisionSession,
} from 'interfaces/types'
import { ArticleStoreProvider } from 'state/article'
import { getAdTag, getCountryAdTag } from 'utils/ads'

import { Breadcrumb } from './breadcrumb'
import { CarePathDefaultContext } from './context'
import { getIconBySession } from './icons'
import useStyles from './index.styles'
import { IntroCarePathModal } from './introCarePathModal'
import RiskScreenerModal, {
  HandlerProps as RiskScreenerModalHandlerProps,
} from './riskScreenerModal'
import { SectionWrapper } from './sectionWrapper'
import {
  DATA_TRACKING_ACTION_BY_SESSION,
  getSpecifySessionBgByPath,
} from './utils'
import { Wrapper } from './wrapper'
import {
  CarePathColorPattern,
  PAGE_USE_TYPE_FORM_TEMPLATE_LIST,
} from '../constant'
import { getSessionsColor, handleScrollToBlock } from '../helpers'

export type CarePathDefaultTemplateProps = {
  spotlight: Spotlight
  hideSticky?: boolean
  stickyNav?: ReactNode
  hasIntroCarepathModal?: boolean
  isAppPage?: boolean
  isInline?: boolean
} & Partial<DynamicSpotlightPageProps> &
  BoxProps

const StickyNavigator = dynamic<StickyNavWrapperProps>(
  () =>
    import(
      './../../../organisms/serviceConnections/stickyNavigator/wrapper'
    ).then((mod) => mod.default),
  {
    ssr: false,
  },
)

const ForwardStickyNavigator = forwardRef(function forwardIdlePopup(
  props: StickyNavigatorProps,
  ref: Ref<StickyNavigatorHandlerProps>,
) {
  return <StickyNavigator {...props} stickyRef={ref} />
})

const HpvBanner = dynamic(
  () => import('./hpvBanner').then((mod) => mod.HpvBanner),
  {
    ssr: false,
  },
)

const IdlePopupWrapper = dynamic<IdlePopupWrapperProps>(
  () =>
    import('components/molecules/idlePopup/wrapper').then((mod) => mod.default),
  {
    ssr: false,
  },
)

const ForwardIdlePopup = forwardRef(function forwardIdlePopup(
  props: IdlePopupProps,
  ref: Ref<IdlePopupHandlerProps>,
) {
  return <IdlePopupWrapper {...props} popupRef={ref} />
})

const ConfirmLeavePage = dynamic<ConfirmLeavePageWapperProps>(
  () =>
    import('components/molecules/confirmLeavePage').then((mod) => mod.default),
  {
    ssr: false,
  },
)

const ForwardConfirmLeavePage = forwardRef(function forwardConfirmLeavePage(
  props: ConfirmLeavePageProps,
  ref: Ref<ConfirmLeavePageHandlerProps>,
) {
  return <ConfirmLeavePage {...props} popupRef={ref} />
})

const ClinicLocator = dynamic<ClinicLocatorProps>(
  () =>
    import('components/organisms/clinicLocator/').then((mod) => mod.default),
  {
    ssr: false,
  },
)

const CarePathDefaultTemplate = ({
  spotlight,
  footerInfo,
  dataNavMenuContent,
  hideSticky,
  stickyNav,
  hasIntroCarepathModal,
  isAppPage,
  styles,
  classNames,
  isInline,
  ...boxProps
}: CarePathDefaultTemplateProps) => {
  const { locale, query, push, pathname, asPath } = useRouterExtend()
  const { classes, cx } = useStyles(undefined, {
    name: 'CarePathDefaultTemplate',
    styles,
    classNames,
  })
  const detectPage = useDetectSCpage(spotlight?.keyID)
  const {
    isNestle,
    isCarePath,
    isRoche,
    isSoya,
    isNan,
    isTaganil,
    isSimilac,
    isTiki,
    isVinamilk,
    isConCung,
    isPfizer,
  } = detectPage
  const activeTheme: CarePathColorPattern = isSoya
    ? {
        primary: '#EC1B2E',
        secondary: '#F1AF55',
        tertiary: '#EC1B2E',
        quaternary: '#EC1B2E',
      }
    : spotlight?.colorPatterns
  const isMobile = useIsMobile()
  const {
    originListSessions,
    heroBanner,
    listRiskscreener,
    listClickable,
    listDoctorEmbedSessions,
    heading,
  } = useMemo(() => {
    let heading = ''
    const listSessions = spotlight.listSessions || []
    const heroBanner: SpotlightHeroBannerSession[] = []
    const listRiskscreener: SpotlightRiskScreenerSession[] = []
    const listClickable: SpotlightClickableBannerSession[] = []
    const listDoctorEmbedSessions: SpotlightEmbeddingDoctorsSession[] = []
    listSessions.map((i: any) => {
      if (HERO_BANNER_SESSION_LAYOUT_LISTS.includes(i?.sessionLayout)) {
        heroBanner.push(i)
      } else {
        if (i.sessionLayout === SessionType.EMBEDDING_RISKSCREENER) {
          listRiskscreener.push(i)
        } else if (i.sessionLayout === SessionType.CLICKABLE_BANNER) {
          listClickable.push(i)
        } else if (i.sessionLayout === SessionType.EMBEDDING_DOCTORS) {
          listDoctorEmbedSessions.push(i)
        }
        if (
          i?.title &&
          !heading &&
          ![
            SessionType.EMBEDDING_RISKSCREENER,
            SessionType.CLINIC_LOCATORS,
            SessionType.LEADGEN,
            SessionType.HERO_BANNER_FULLWIDTH,
          ].includes(i.sessionLayout)
        ) {
          heading = i.sessionId
        }
      }
    })

    return {
      originListSessions: listSessions,
      heroBanner: heroBanner?.[0],
      listRiskscreener,
      listClickable,
      listDoctorEmbedSessions,
      heading,
    }
  }, [])
  const navigating = heroBanner?.navigating || []
  const listSessionsGroupByNav = useMemo(() => {
    const tmp: Array<Array<SpotlightSessionType>> = []
    const navigatingId = navigating.map((i) => i.idScroll)
    originListSessions.map((i, idx) => {
      if (heroBanner && idx === 0) {
        return
      }
      if (navigatingId.includes(i.idScroll)) {
        tmp.push([i])
      } else {
        if (tmp.length === 0) {
          tmp.push([i])
        } else {
          tmp[tmp.length - 1] && tmp[tmp.length - 1].push(i)
        }
      }
    })
    return tmp
  }, [originListSessions])

  const listSessionsByLoaded = useRef(
    originListSessions.slice(0, -1).map((i: SpotlightSessionType) => ({
      idScroll: i.idScroll,
      loaded: !(
        i.sessionLayout ===
        (SessionType.EMBEDDING_RISKSCREENER ||
          SessionType.HERO_BANNER_FULLWIDTH)
      ),
    })),
  )
  const hasDoctorEmbedSessions = listDoctorEmbedSessions.length > 0
  const leadgenRef = useRef<any>({})
  const scrollToHash = useRef('')
  const nonBgList = useMemo(
    () => [
      SessionType.ARTICLES,
      SessionType.EMBEDDING_RISKSCREENER_MULTI,
      SessionType.CUSTOMIZED_BLOCK,
    ],
    [],
  )
  const colorArr = getSessionsColor(navigating, activeTheme)
  const heroBannerRef = useRef<HTMLDivElement>(null)
  const riskScreenerRefs = useRef<HTMLDivElement[]>([])
  const scrollingRef = useRef<ScrollingBlocksHandlerProps | null>(null)
  const stickyNavRef = useRef<StickyNavigatorHandlerProps | null>(null)
  const idlePopupRef = useRef<IdlePopupHandlerProps | null>(null)
  const confirmLeavePageRef = useRef<ConfirmLeavePageHandlerProps | null>(null)
  const riskScreenerModalRef = useRef<RiskScreenerModalHandlerProps | null>(
    null,
  )
  const chatbotRef = useRef<AIChatBoHandlerProps>(null)
  const doctorListRef = useRef<CarePathDoctorListHandlerProps[]>([])
  const dectectPage = useDetectSCpage(spotlight?.keyID)
  const mappingIconList = getIconBySession(dectectPage)
  const hasMapVisibleTracking = useRef(false)
  const typeFormObj = useMemo(() => {
    const curObj = PAGE_USE_TYPE_FORM_TEMPLATE_LIST.filter(
      (i) =>
        i.asPath.toLowerCase() === encodeURI(spotlight.permalink).toLowerCase(),
    )
    return curObj.length > 0
      ? curObj[0]
      : {
          mobileFormId: '',
          desktopFormId: '',
        }
  }, [])
  const currentUrl = useCurrentUrl()

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: {
      result: 'Spotlight',
      redundant: true,
    },
  })

  const leadgenStyles = (theme: MantineTheme) => ({
    ...(isSimilac &&
      asPath !== '/chien-binh-me-sinh-mo/me-mang-thai/' && {
        root: {
          [theme.fn.smallerThan('sm')]: {
            background: theme.colors.neutral[0],
            '.mantine-Form-bannerWrapper': {
              margin: 0,
            },
            '.mantine-Form-formWrapper': {
              width: '100%',
            },
          },
          [theme.fn.largerThan('sm')]: {
            '.mantine-Form-formWrapper': {
              width: 500,
            },
          },
        },
        wrapper: {
          borderRadius: theme.radius.lg,
          '.mantine-Form-formRoot': {
            width: '100%',
          },
          [theme.fn.largerThan('sm')]: {
            backgroundColor: '#fff',
            boxShadow: theme.shadows.sm,
            borderRadius: theme.radius.lg,
            '.mantine-Form-formRoot': {
              width: '100%',
            },
            '.mantine-Form-scrollAreaViewport': {
              paddingRight: 20,
            },
          },
        },
        formFrame: {
          [theme.fn.largerThan('sm')]: {
            border: 0,
            padding: 0,
            margin: 0,
          },
        },
        container: {
          [theme.fn.largerThan('sm')]: {
            justifyContent: 'space-between',
            padding: '32px 0 32px 60px',
          },
        },
      }),
    ...(isSoya && {
      root: {
        '&[data-inline="true"]': {
          [theme.fn.largerThan('sm')]: {
            paddingBottom: 0,
          },
        },
      },
      modalBody: {
        backgroundColor: theme.colors.neutral[0],
      },
      bannerImg: {
        [theme.fn.largerThan('sm')]: {
          maxWidth: rem(360),
        },
      },
      formFrame: {
        [theme.fn.largerThan('sm')]: {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          padding: 0,
          maxWidth: 'none',
        },
      },
      container: {
        [theme.fn.largerThan('sm')]: {
          justifyContent: 'space-between',
          gap: 100,
        },
      },
    }),
  })

  const getIndexByIdScroll = useCallback(
    (idScroll: string) =>
      listSessionsByLoaded.current.findIndex((i) => i.idScroll === idScroll),
    [listSessionsByLoaded],
  )

  const handleScrollToHash = useCallback(
    ({ idScroll, loaded = false }: { idScroll: string; loaded?: boolean }) => {
      const curIdx = getIndexByIdScroll(idScroll)
      if (curIdx > -1) {
        if (loaded) {
          listSessionsByLoaded.current[curIdx].loaded = true
        }
        const listSessNotLoadYet = listSessionsByLoaded.current
          .slice(0, curIdx + 1)
          .filter((i) => !i.loaded)
        if (listSessNotLoadYet.length === 0) {
          onItemClick(scrollToHash.current)
          scrollToHash.current = ''
        }
      }
    },
    [],
  )

  useEffect(() => {
    const hash = (window.location.hash || '').substring(1)
    if (hash) {
      scrollToHash.current = (window.location.hash || '').substring(1)
      setTimeout(() => handleScrollToHash({ idScroll: hash }), 1000)
      // handleScrollToHash({ idScroll: hash })
    } else {
      scrollingRef.current?.setCanScroll(true)
    }

    const bodyEle = document.getElementsByTagName('body')[0]
    bodyEle.classList.add('carepath-body', 'ai-chatbot')
    isRoche &&
      bodyEle.classList.add(
        'hpv-page',
        'chatwoot-expand-style',
        'replace-chatwoot-icon',
      )
    // isTaganil &&
    //   bodyEle.classList.add(
    //     'taganil-page',
    //     'chatwoot-expand-style',
    //     'replace-chatwoot-icon',
    //   )
    return () => {
      bodyEle.classList.remove('carepath-body')
      isRoche &&
        bodyEle.classList.remove(
          'hpv-page',
          'chatwoot-expand-style',
          'replace-chatwoot-icon',
        )
      // isTaganil &&
      //   bodyEle.classList.remove(
      //     'taganil-page',
      //     'chatwoot-expand-style',
      //     'replace-chatwoot-icon',
      //   )
    }
  }, [])

  const onItemClick = (hash: string) => {
    scrollingRef.current?.setCanScroll(false)
    handleScrollToBlock({
      hash: (hash.includes('%')
        ? hash
        : encodeURIComponent(hash)
      ).toLowerCase(),
      isMobile,
      callback: () => scrollingRef.current?.setCanScroll(true),
    })
  }

  const commonBannerSession = {
    colorArr,
    mappingIconList,
  }

  const navigatorProps = {
    onNavClick: (
      i: ScrollToItemType,
      e: MouseEvent<HTMLAnchorElement | HTMLDivElement>,
    ) => {
      if (i.sessionLayout === SessionType.TOP) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      } else if (i.sessionLayout === SessionType.LEADGEN) {
        e?.preventDefault()
        if (leadgenRef?.current?.[i.idScroll]) {
          leadgenRef.current[i.idScroll].openModal()
        } else {
          onItemClick(i.idScroll)
        }
      } else if (i.sessionLayout === SessionType.EMBEDDING_RISKSCREENER) {
        e?.preventDefault()
        riskScreenerModalRef?.current?.open()
      } else if (i.sessionLayout === SessionType.SOYA_OPEN_CHAT) {
        e?.preventDefault()
        chatbotRef?.current?.setShowWidget(true)
      } else if (!i.url) {
        e?.preventDefault()
        onItemClick(i.idScroll)
      }
    },
  }

  const commonBannerNavProps = {
    mappingIconList,
    dataActionBySession: DATA_TRACKING_ACTION_BY_SESSION,
    onItemClick,
    // openChatFn,
    ...navigatorProps,
  }
  const customTargetings = {
    Is_Sponsored: [1],
    Is_Expert: [spotlight?.expert?.experts?.[0]?.id ? 1 : 0],
    Is_Affiliate: [0],
    // [spotlight?.hasAffiliate ? 1 : 0],
    Brand: spotlight?.sponsor?.map((s) => innertext(s.title || '')) || [],
  }

  const hasSponsor = (spotlight.sponsor || []).length > 0
  const hasBreadcrumb = spotlight.listGroup.length > 1
  const mastheadEnable = useMastheadAdEnable(spotlight.adsOnArticles || {})
  const handleStopTrackingIdle = (opened: boolean) => {
    if (opened) {
      idlePopupRef?.current?.close()
      idlePopupRef?.current?.stopTracking(true)
    } else {
      idlePopupRef?.current?.stopTracking(false)
    }
  }

  const stickyNavEle = (
    <ForwardStickyNavigator
      ref={stickyNavRef}
      {...{
        listSessions: originListSessions,
        navigating,
        colorShade: activeTheme,
        colorArr: isSoya
          ? ['#F1AF55', '#EC1B2E', '#EC1B2E', '#EC1B2E']
          : colorArr,
        activeColor: activeTheme.primary,
        mappingIconList,
        onItemClick,
        sponsoredBy: spotlight.sponsor,
        isAppPage,
        logo: spotlight?.logo,
        ...navigatorProps,
      }}
      dataActionBySession={(() => {
        const temp: { [key in SessionType]?: string } = {}
        Object.keys(DATA_TRACKING_ACTION_BY_SESSION).map((i) => {
          temp[i as SessionType] = `${
            DATA_TRACKING_ACTION_BY_SESSION[i as SessionType]
          } - Header Navigate`
        })
        return temp
      })()}
      mappingIconList={mappingIconList}
      styles={() => ({
        sponsor: {
          ...(!hasBreadcrumb && {
            order: 1,
          }),
        },
        navigator: {
          ...(!hasBreadcrumb && {
            order: 2,
          }),
        },
      })}
    >
      {stickyNav}
    </ForwardStickyNavigator>
  )

  return (
    <CarePathDefaultContext.Provider
      value={{
        colorArr,
        navigating,
        mappingIconList,
        detectPage,
        activeTheme,
        keyID: spotlight?.keyID || '',
      }}
    >
      <GTMScript
        servicePageTrackingParams={{
          pageId: spotlight.postName,
          audienceId: spotlight.groupIdActive || '',
          funnelId: spotlight.additionalSlugActive || '',
        }}
      />
      <NavigatorLayout
        title={spotlight?.postTitle}
        description={spotlight?.postTitle}
        image={LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
        isSponsored={isMobile && spotlight?.isSponsor}
        sponsoredType="content"
        sponsoredBy={isMobile ? spotlight?.sponsor : undefined}
        // isHomePage={false}
        SEOData={{
          title: spotlight?.postTitle,
          description: spotlight?.postTitle,
          apiMetaData: spotlight?.metaTag,
          isIndexable: true,
        }}
        type="profile"
        footerInfo={footerInfo}
        languageOptions={spotlight?.translateOptions || []}
        dataNavMenuContent={dataNavMenuContent}
        subHead={hideSticky ? undefined : stickyNavEle}
        isInline={isInline}
      >
        <ArticleStoreProvider
          init={{
            currentArticle: (spotlight as unknown) as Article,
          }}
        >
          {/* https://hhgdev.atlassian.net/browse/HHD-6441 */}
          <AdBlockContainer>
            <MastheadAd
              adUnitPath={adUnitPath}
              className={`spotlight-masthead ${spotlight?.type}-masthead`}
              hasPlaceholder={mastheadEnable}
            />
          </AdBlockContainer>
          <StyledLandingWrapper
            className={cx(classes.root, 'service-connection-page')}
            style={{ minHeight: rem(610), marginBottom: 0 }}
          >
            <Wrapper
              className={classes.wrapper}
              spotlight={spotlight}
              detectPage={dectectPage}
              activeTheme={activeTheme}
              {...boxProps}
            >
              <ScrollingBlocks
                className={classes.sectionList}
                ref={scrollingRef}
                disableScrollTo
                onVisible={(visibleSection) => {
                  stickyNavRef?.current?.setSelectedSection(visibleSection)
                  let curVisibleSection = ''
                  const curVisileIndex = navigating.findIndex((i) => {
                    if (i.idScroll === visibleSection) {
                      curVisibleSection = i.sessionLayout
                      return true
                    }
                    return false
                  })
                  if (
                    curVisibleSection === SessionType.EMBEDDING_RISKSCREENER
                  ) {
                    handleStopTrackingIdle(true)
                  }
                  const selectedSectionInfo =
                    curVisileIndex === navigating.length - 1
                      ? navigating?.[0]
                      : navigating[curVisileIndex + 1]

                  confirmLeavePageRef?.current?.setSuggestSection({
                    hash: selectedSectionInfo.idScroll,
                    cta: selectedSectionInfo?.title,
                    btnTracking: {
                      dataEventCategory: 'Service Connection',
                      dataEventAction: `${
                        DATA_TRACKING_ACTION_BY_SESSION[
                          selectedSectionInfo.sessionLayout
                        ] || ''
                      } - Intent popup`,
                      dataEventLabel: `${currentUrl.split('#')[0]}#${decodeURI(
                        selectedSectionInfo.idScroll,
                      )}`,
                    },
                  })
                }}
              >
                {heroBanner &&
                  (() => {
                    const i = heroBanner
                    const key = i.sessionLayout as SessionType
                    const isCtaBanner =
                      key.includes(SessionType.HERO_BANNER_DEFAULT) &&
                      i.isCtaBanner
                    return (
                      <Box
                        key={i.sessionLayout}
                        data-id-scroll={''}
                        data-section-id={key}
                        sx={(theme) => ({
                          ...(isCtaBanner && {
                            backgroundColor: theme.colors.neutral[0],
                          }),
                        })}
                      >
                        <Container>
                          <Box
                            sx={(theme) => ({
                              [theme.fn.largerThan('sm')]: {
                                display: 'flex',
                                justifyContent: 'space-between',
                              },
                            })}
                          >
                            {hasBreadcrumb ? (
                              <Breadcrumb spotlight={spotlight} />
                            ) : heroBanner.isCtaBanner ? (
                              <Box
                                sx={(theme) => ({
                                  height: rem(20),
                                  [theme.fn.largerThan('sm')]: {
                                    height: rem(40),
                                  },
                                })}
                              ></Box>
                            ) : null}
                            {hasSponsor && !isMobile && (
                              <StickySponsoredBy
                                isCollapsed={false}
                                sponsoredType={'content'}
                                sponsoredBy={spotlight.sponsor}
                                isMobile={isMobile}
                                styles={(theme) => ({
                                  root: {
                                    position: 'relative',
                                    zIndex: 1,
                                    backgroundColor: 'transparent',
                                    border: 0,
                                    [theme.fn.largerThan('sm')]: {
                                      padding: 0,
                                    },
                                  },
                                  logoWrapper: {
                                    marginLeft: rem(12),
                                  },
                                  logo: {
                                    [theme.fn.largerThan('sm')]: {
                                      height: rem(32),
                                    },
                                  },
                                  container: {
                                    [theme.fn.largerThan('sm')]: {
                                      padding: 0,
                                    },
                                  },
                                })}
                              />
                            )}
                          </Box>
                        </Container>
                        <Box ref={heroBannerRef} data-bg-session={true}>
                          {heroBanner.sessionLayout ===
                          SessionType.HERO_BANNER_APP ? (
                            <AppIntroHeroBanner
                              title={heroBanner.title}
                              banner={(heroBanner as any)?.banner?.desktop}
                              headerDescription={
                                (heroBanner as any).headerDescription
                              }
                              innerClass={'wide-container'}
                              descInnerClass={'narrow-container'}
                              logo={spotlight?.logo}
                              onNavClick={commonBannerNavProps.onNavClick}
                            />
                          ) : (
                            <HeroBanner
                              {...commonBannerSession}
                              ratio={isMobile && isTiki ? 375 / 200 : undefined}
                              onItemClick={onItemClick}
                              hasSociaShare={spotlight?.hasSocialShare}
                              layout={(props) =>
                                isCtaBanner ? (
                                  <CtaBanner
                                    {...props}
                                    mappingIconList={mappingIconList}
                                    listBanner={
                                      (i as SpotlightHeroBannerSession)
                                        .listBanner
                                    }
                                    navigatorProps={
                                      {
                                        ...commonBannerNavProps,
                                        itemComponent: function Item(
                                          props: BlockItemPropsType,
                                        ) {
                                          return (
                                            <FullBg
                                              {...props}
                                              useImgAsFullBg={isNestle || isNan}
                                              showBrushPattern={
                                                !isMobile && isCarePath
                                              }
                                              {...(isNestle && {
                                                isQuare: false,
                                              })}
                                              styles={() => ({
                                                ...(isNestle && {
                                                  root: {
                                                    ...(navigating.length ===
                                                      3 && {
                                                      '&:first-of-type': {
                                                        width: '100%',
                                                      },
                                                    }),
                                                  },
                                                  exploreLink: {
                                                    color: '#FAEEC3',
                                                  },
                                                }),
                                              })}
                                            />
                                          )
                                        },
                                      } as any
                                    }
                                    autoSlide={Boolean(
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming,
                                    )}
                                    delayTime={
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming
                                    }
                                  />
                                ) : key.includes(
                                    SessionType.HERO_BANNER_FULLWIDTH,
                                  ) ? (
                                  <CustomBanner
                                    {...props}
                                    autoSlide={Boolean(
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming,
                                    )}
                                    delayTime={
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming
                                    }
                                    navigatorProps={
                                      {
                                        ...commonBannerNavProps,
                                        itemComponent: isRoche
                                          ? ImageIcon
                                          : isCarePath || isSimilac
                                          ? ElegantUI
                                          : (props: any) => (
                                              <FlatIcon
                                                {...props}
                                                useBgColor={isSoya}
                                                styles={(theme) => ({
                                                  ...(isSoya && {
                                                    root: {
                                                      '&[data-type="comming_soon"]': {
                                                        position: 'relative',
                                                        cursor: 'default',
                                                        '&:before': {
                                                          content: '""',
                                                          position: 'absolute',
                                                          backgroundSize:
                                                            'contain',
                                                          backgroundRepeat:
                                                            'no-repeat',
                                                          zIndex: 1,
                                                          [theme.fn.largerThan(
                                                            'sm',
                                                          )]: {
                                                            top: '-3%',
                                                            left: '5%',
                                                            width: '99%',
                                                            height: '0',
                                                            paddingBottom:
                                                              '80%',
                                                            backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                              'soya/comming-soon.png',
                                                            )})`,
                                                          },
                                                          [theme.fn.smallerThan(
                                                            'sm',
                                                          )]: {
                                                            top: rem(-5),
                                                            left: rem(13),
                                                            width: '65%',
                                                            height: '0',
                                                            paddingBottom:
                                                              '34%',
                                                            backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                              'soya/comming-soon-mobile.png',
                                                            )})`,
                                                          },
                                                        },
                                                        p: {
                                                          opacity: 0.5,
                                                          filter: 'blur(4px)',
                                                          [theme.fn.largerThan(
                                                            'sm',
                                                          )]: {
                                                            filter: 'blur(6px)',
                                                          },
                                                        },
                                                        '.img-wrapper': {
                                                          opacity: 0.5,
                                                        },
                                                      },
                                                    },
                                                  }),
                                                })}
                                              />
                                            ),
                                      } as any
                                    }
                                    onBannerLoad={() =>
                                      scrollToHash.current &&
                                      handleScrollToHash({
                                        idScroll: i.idScroll,
                                        loaded: true,
                                      })
                                    }
                                  />
                                ) : (
                                  <DefaultBanner
                                    {...props}
                                    navigatorProps={
                                      {
                                        ...commonBannerNavProps,
                                        itemComponent: isRoche
                                          ? ImageIcon
                                          : isCarePath
                                          ? ElegantUI
                                          : FlatIcon,
                                      } as any
                                    }
                                  />
                                )
                              }
                              sessionProps={i as SpotlightHeroBannerSession}
                              styles={(theme) => ({
                                mainBannerCtaBtn: {
                                  [theme.fn.smallerThan('sm')]: {
                                    ...(isRoche && {
                                      paddingLeft: 0,
                                      paddingRight: 0,
                                    }),
                                  },
                                },
                                mainBannerCtaBtnRightIcon: {
                                  [theme.fn.smallerThan('sm')]: {
                                    ...(isRoche && {
                                      display: 'none',
                                    }),
                                  },
                                },
                                navigatorWrapper: {
                                  ...(isSoya && {
                                    backgroundColor: 'transparent',
                                  }),
                                },
                              })}
                              // onClickRiskScreener={() =>
                              //   riskScreenerModalRef?.current?.open()
                              // }
                            />
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                {(() => {
                  let countArticle = 0
                  let countClickable = 0
                  return listSessionsGroupByNav.map(
                    (listSessionsInside, mainIdx) => {
                      const mainSession = listSessionsInside[0]
                      const mainKey = mainSession?.idScroll as SessionType
                      return (
                        <Box
                          key={mainKey}
                          // data-section-id={mainKey}
                          data-bg-session={nonBgList.includes(mainKey)}
                          data-id-scroll={mainKey}
                          data-section-index={mainIdx}
                          className={classes.section}
                        >
                          {listSessionsInside.map((i, idx) => {
                            const key = i.sessionLayout as SessionType
                            return (
                              <Box
                                id={`${i.idScroll}`}
                                {...(i.sessionLayout ===
                                  SessionType.CONTENT_BANNER && {
                                  'data-vertical-layout': true,
                                })}
                                key={mainKey}
                                data-hashtag={i.hashtag || i.idScroll}
                                data-none-bg-session={nonBgList.includes(key)}
                                data-section-sub-index={idx}
                                data-gap={
                                  !nonBgList.includes(key)
                                    ? isMobile
                                      ? 40
                                      : 49
                                    : 0
                                }
                                data-section-id={i.hashtag || i.idScroll}
                                {...(isSimilac &&
                                  key === SessionType.LEADGEN && {
                                    'data-lead-si': 'true',
                                  })}
                                {...(isSimilac &&
                                  key === SessionType.QUOTATION && {
                                    'data-quo-si': 'true',
                                  })}
                              >
                                {key.includes(SessionType.EMBEDDING_DOCTORS) ? (
                                  (() => {
                                    const curSession = i as SpotlightEmbeddingDoctorsSession
                                    return (
                                      <CarePathDoctorList
                                        ref={(el) =>
                                          el && doctorListRef.current.push(el)
                                        }
                                        defaultColor={activeTheme.tertiary}
                                        hidePrice={isCarePath || isTaganil}
                                        hideViewAll={
                                          isCarePath && locale !== 'id-ID'
                                        }
                                        applyH1={
                                          curSession.sessionId === heading
                                        }
                                        viewAllUrl={
                                          isCarePath && locale === 'id-ID'
                                            ? curSession.typeEmbedding ===
                                              'doctors'
                                              ? `https://${domainLocales[locale]}/care/semua/dokter/spesialis/`
                                              : curSession.typeEmbedding ===
                                                  'top_service' ||
                                                curSession.typeEmbedding ===
                                                  'specialties'
                                              ? `https://${domainLocales[locale]}/care/semua/rumah-sakit/spesialis/`
                                              : undefined
                                            : undefined
                                        }
                                        listingAll={curSession.doctors}
                                        filterColor={
                                          isRoche ? '#00BEBF' : undefined
                                        }
                                        hideBgPattern={isRoche}
                                        showConfirmCityPopup={isRoche}
                                        // showPromotionEvent={isRoche}
                                        layoutConfig={
                                          isRoche
                                            ? {
                                                1: 'wide-horizon',
                                                2: 'wide-horizon',
                                              }
                                            : undefined
                                        }
                                        campaign={curSession.campaign}
                                        sessionProps={{
                                          ...curSession,
                                          backgroundColor:
                                            getSpecifySessionBgByPath(
                                              isRoche,
                                            )?.[
                                              SessionType.EMBEDDING_DOCTORS
                                            ] || i.backgroundColor,
                                        }}
                                        styles={{
                                          ...(isRoche && {
                                            heading: {
                                              color: activeTheme.primary,
                                            },
                                            processBar: {
                                              background: activeTheme.primary,
                                              opacity: 0.4,
                                            },
                                            processRoot: {
                                              background: '#fff',
                                            },
                                            listing: {
                                              '&[data-slide-length="2"]': {
                                                flexDirection: 'column',
                                              },
                                            },
                                          }),
                                        }}
                                      />
                                    )
                                  })()
                                ) : key.includes(
                                    SessionType.CLINIC_LOCATORS,
                                  ) ? (
                                  <SectionWrapper
                                    // sx={{
                                    //   minHeight: '70vh',
                                    // }}
                                    onInView={(inView) => {
                                      if (
                                        inView &&
                                        !hasMapVisibleTracking.current
                                      ) {
                                        window.gtag?.('event', 'visible', {
                                          event_category: 'Service Connection',
                                          event_action:
                                            'Clinic Directory Click',
                                          event_label: currentUrl,
                                          audience: spotlight.groupIdActive,
                                        })
                                        hasMapVisibleTracking.current = true
                                      }
                                    }}
                                  >
                                    <ClinicLocator
                                      title={
                                        (i as SpotlightClinicLocatorSession)
                                          .title
                                      }
                                      specialties={
                                        (i as SpotlightClinicLocatorSession)
                                          .specialties
                                      }
                                      defaultColor={
                                        colorArr?.[
                                          navigating.findIndex(
                                            (j) => j.idScroll === i.idScroll,
                                          )
                                        ] || activeTheme.primary
                                      }
                                      infoPath={
                                        isCarePath && locale === LOCALE.Taiwan
                                          ? 'organization.name'
                                          : undefined
                                      }
                                      funnelId={spotlight.additionalSlugActive}
                                      onGetBrowserLoc={(browserLatLng) => {
                                        hasDoctorEmbedSessions &&
                                          browserLatLng &&
                                          (async () => {
                                            const res = await callApiNew(
                                              PATHS.CARE.GET_CITY_ID_BY_LAT_LNG,
                                              {
                                                params: browserLatLng,
                                              },
                                              'care',
                                            )
                                            const resData = res?._data?.default
                                            resData?.id &&
                                              doctorListRef.current.map((i) => {
                                                i.setSelectedFilterId(
                                                  String(resData.id),
                                                )
                                              })
                                          })()
                                      }}
                                    />
                                  </SectionWrapper>
                                ) : key.includes(SessionType.VIDEOS) ||
                                  key.includes(SessionType.VIDEO_SECTION) ? (
                                  (i as SpotlightVideoSession).isShortReel ? (
                                    <ShortReelList
                                      sessionProps={{
                                        ...(i as SpotlightVideoSession),
                                        backgroundColor:
                                          (isRoche
                                            ? getSpecifySessionBgByPath(
                                                isRoche,
                                              )?.[SessionType.VIDEOS]
                                            : isSoya
                                            ? 'transparent'
                                            : i.backgroundColor) || '',
                                      }}
                                    />
                                  ) : (
                                    <VideoLibrary
                                      idScroll={
                                        (i as SpotlightVideoSession).idScroll ||
                                        'embed-video' + idx
                                      }
                                      data-id-scroll={isSoya ? 'aivideo' : ''}
                                      autoPlay={
                                        (i as SpotlightVideoSession).autoPlay
                                      }
                                      applyH1={
                                        !isAppPage &&
                                        (i as SpotlightVideoSession)
                                          .sessionId === heading
                                      }
                                      sessionProps={{
                                        ...(i as SpotlightVideoSession),
                                        backgroundColor:
                                          (isRoche
                                            ? getSpecifySessionBgByPath(
                                                isRoche,
                                              )?.[SessionType.VIDEOS]
                                            : isSoya
                                            ? 'transparent'
                                            : i.backgroundColor) || '',
                                      }}
                                    />
                                  )
                                ) : key.includes(
                                    SessionType.EMBEDDING_RISKSCREENER,
                                  ) ? (
                                  <Box
                                    ref={(e: HTMLDivElement) => {
                                      e && riskScreenerRefs.current.push(e)
                                    }}
                                  >
                                    <RiskScreenerEmbed
                                      data={{
                                        ...(i as SpotlightRiskScreenerSession),
                                        backgroundColor: isNestle
                                          ? 'transparent'
                                          : getSpecifySessionBgByPath(
                                              isRoche,
                                            )?.[
                                              SessionType.EMBEDDING_RISKSCREENER
                                            ] || i.backgroundColor,
                                      }}
                                      isFullContainer
                                      primaryColor={activeTheme.primary}
                                      onLoad={(loaded: boolean) => {
                                        loaded &&
                                          scrollToHash.current &&
                                          handleScrollToHash({
                                            idScroll: i.idScroll,
                                            loaded: true,
                                          })
                                      }}
                                      typeoformId={
                                        isMobile
                                          ? typeFormObj.mobileFormId
                                          : typeFormObj.desktopFormId
                                      }
                                    />
                                  </Box>
                                ) : key.includes(
                                    SessionType.EMBEDDING_RISKSCREENER_MULTI,
                                  ) ? (
                                  <MultiRiskScreenerEmbed
                                    colorShade={activeTheme}
                                    sessionProps={{
                                      ...(i as SpotlightMultiRiskScreenerSession),
                                      backgroundColor:
                                        getSpecifySessionBgByPath(isRoche)?.[
                                          SessionType
                                            .EMBEDDING_RISKSCREENER_MULTI
                                        ] || i.backgroundColor,
                                    }}
                                    applyH1={
                                      (i as SpotlightRiskScreenerSession)
                                        .sessionId === heading
                                    }
                                  />
                                ) : key.includes(SessionType.ARTICLES) ? (
                                  <Box
                                    data-is-article={true}
                                    sx={(theme) => ({
                                      ...((isRoche || isSoya) && {
                                        '.card-item': {
                                          background: 'transparent',
                                        },
                                      }),
                                      ...(isSoya && {
                                        backgroundColor:
                                          theme.colors.neutral[0],
                                        [theme.fn.smallerThan('sm')]: {
                                          margin: `-40px 0`,
                                          padding: '40px 0',
                                        },
                                        [theme.fn.largerThan('sm')]: {
                                          margin: `-80px 0`,
                                          padding: '80px 0',
                                        },
                                      }),
                                    })}
                                  >
                                    <ArticleCardList
                                      listCategories={
                                        (i as SpotlightArticleSession)
                                          .listCategories
                                      }
                                      wrapperProps={
                                        (i as SpotlightArticleSession)
                                          .wrapperProps
                                      }
                                      display={
                                        (i as SpotlightArticleSession)
                                          .typeDisplay
                                      }
                                      articles={
                                        (i as SpotlightArticleSession)
                                          .listArticles
                                      }
                                      listingByKeyPillar={
                                        (i as SpotlightArticleSession)
                                          .listingByKeyPillar
                                      }
                                      readmoreArticles={
                                        (i as SpotlightArticleSession)
                                          .readMoreArticles
                                      }
                                      adUnitPath={
                                        (i as SpotlightArticleSession)
                                          .typeDisplay !== 'advance' &&
                                        !spotlight.disableAd
                                          ? countArticle++ === 0
                                            ? adUnitPath
                                            : undefined
                                          : undefined
                                      }
                                      applyH1={
                                        (i as SpotlightArticleSession)
                                          .sessionId === heading
                                      }
                                      mobileLayoutStretch={isPfizer}
                                      styles={{
                                        ...(isRoche && {
                                          processRoot: {
                                            background: '#fff',
                                            padding: 0,
                                          },
                                          processBar: {
                                            background: activeTheme.primary,
                                            opacity: 0.4,
                                          },
                                        }),
                                      }}
                                    />
                                  </Box>
                                ) : key.includes(
                                    SessionType.CLICKABLE_BANNER,
                                  ) ||
                                  key.includes(SessionType.CHAT_WITH_EXPERT) ? (
                                  (i as SpotlightClickableBannerSession)
                                    .isSingleBanner ? (
                                    <ClickableSingleBanner
                                      data={
                                        i as SpotlightClickableSingleBannerSession
                                      }
                                      onItemClick={onItemClick}
                                      data-id-scroll={i.hashtag}
                                      applyH1={
                                        (i as SpotlightClickableSingleBannerSession)
                                          .sessionId === heading
                                      }
                                      styles={(theme) => ({
                                        root: {
                                          backgroundColor: isNan
                                            ? '#faf4e6'
                                            : (i as SpotlightClickableSingleBannerSession)
                                                .backgroundColor,
                                          ...(isNan && {
                                            backgroundImage: `url(${CommonGAssets.getAssetPath(
                                              'nan/rs-bg-mobile.jpg',
                                            )})`,
                                            backgroundSize: 'cover',
                                            [theme.fn.largerThan('sm')]: {
                                              backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                'nan/rs-bg.jpg',
                                              )})`,
                                              backgroundPosition:
                                                'bottom right',
                                            },
                                          }),
                                        },
                                        disclaimerIcon: {
                                          path: {
                                            fill: isNan
                                              ? activeTheme.quaternary
                                              : activeTheme.primary,
                                          },
                                        },
                                      })}
                                    />
                                  ) : (
                                    <ClickableBanner
                                      requireLogin={
                                        (i as SpotlightClickableBannerSession)
                                          .requireLogin
                                      }
                                      applyCarousel={
                                        (i as SpotlightClickableBannerSession)
                                          .applyCarousel
                                      }
                                      embedChatwootClick={Boolean(
                                        key.includes(
                                          SessionType.CHAT_WITH_EXPERT,
                                        ) || isRoche,
                                      )}
                                      bgColor={
                                        getSpecifySessionBgByPath(isRoche)?.[
                                          SessionType.CLICKABLE_BANNER
                                        ] ||
                                        (i as SpotlightClickableBannerSession)
                                          .backgroundColor
                                      }
                                      banner={{
                                        title: (i as SpotlightClickableBannerSession)
                                          .title,
                                        description: (i as SpotlightClickableBannerSession)
                                          .description,
                                        listing: (i as SpotlightClickableBannerSession)
                                          .listing,
                                        list_banners: [],
                                        icon: '',
                                      }}
                                      chatToken={
                                        (i as SpotlightClickableBannerSession)
                                          .token
                                      }
                                      avatarIcon={
                                        (i as SpotlightClickableBannerSession)
                                          .avatarIcon
                                      }
                                      bannerProps={
                                        {
                                          'data-event-category':
                                            'Service Connection',
                                          'data-event-action':
                                            'Clickable Banner',
                                        } as BannerProps
                                      }
                                      hasDisclaimer={
                                        isCarePath &&
                                        !key.includes(
                                          SessionType.CHAT_WITH_EXPERT,
                                        )
                                      }
                                      onItemClick={onItemClick}
                                      applyH1={
                                        (i as SpotlightClickableBannerSession)
                                          .sessionId === heading
                                      }
                                      openAllAccordion={
                                        (i as SpotlightClickableBannerSession)
                                          .expandAllCategories
                                      }
                                      styles={(theme) => ({
                                        ...(isNan && {
                                          root: {
                                            backgroundColor: '#faf4e6',
                                            backgroundImage: `url(${CommonGAssets.getAssetPath(
                                              'nan/session-bg-mobile.jpg',
                                            )})`,
                                            backgroundSize: 'cover',
                                            [theme.fn.largerThan('sm')]: {
                                              backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                'nan/session-bg.jpg',
                                              )})`,
                                            },
                                          },
                                        }),
                                        ...(isConCung && {
                                          root: {
                                            backgroundImage: `url(${CommonGAssets.getAssetPath(
                                              'concung/doctor-bg-m.jpg',
                                            )})`,
                                            backgroundSize: 'cover',
                                            [theme.fn.largerThan('sm')]: {
                                              backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                'concung/doctor-bg.jpg',
                                              )})`,
                                            },
                                          },
                                          bannerList: {
                                            [theme.fn.smallerThan('sm')]: {
                                              flexDirection: 'column',
                                              gap: 0,
                                            },
                                          },
                                          bannerItem: {
                                            width: '100%',
                                            [theme.fn.largerThan('sm')]: {
                                              width: 400,
                                            },
                                          },
                                          heading: {
                                            background:
                                              'linear-gradient(180deg, #253B75 24.37%, #2089C8 61.44%, #4274B7 73.15%, #253B75 91.68%)',
                                            '-webkit-background-clip': 'text',
                                            '-webkit-text-fill-color':
                                              'transparent',
                                          },
                                        }),
                                        ...(Boolean(
                                          spotlight.integrates3rdParty
                                            .chatwoodToken,
                                        ) && {
                                          root: {
                                            ...(isRoche && {
                                              [theme.fn.largerThan('md')]: {
                                                paddingBottom: 0,
                                                position: 'relative',
                                                ...(countClickable === 0 && {
                                                  '&:before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '9%',
                                                    left: '4%',
                                                    zIndex: 0,
                                                    width: rem(181),
                                                    height: rem(216),
                                                    backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                      'roche/cancer-bg1.png',
                                                    )})`,
                                                  },
                                                }),
                                                ...(countClickable++ ===
                                                  listClickable.length - 1 && {
                                                  '&:after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 0,
                                                    zIndex: 0,
                                                    width: rem(181),
                                                    height: rem(216),
                                                    backgroundImage: `url(${CommonGAssets.getAssetPath(
                                                      'roche/cancer-bg2.png',
                                                    )})`,
                                                  },
                                                }),
                                              },
                                            }),
                                          },
                                          inner: {
                                            position: 'relative',
                                            zIndex: 1,
                                          },
                                        }),
                                        // ...(isTaganil && {
                                        //   bubbleInside: {
                                        //     borderColor: activeTheme.primary,
                                        //   },
                                        //   bubbleText: {
                                        //     color: activeTheme.primary,
                                        //   },
                                        // }),
                                        header: {
                                          textAlign: key.includes(
                                            SessionType.CHAT_WITH_EXPERT,
                                          )
                                            ? 'center'
                                            : (
                                                (i as SpotlightClickableBannerSession)
                                                  .listing || []
                                              ).length > 3
                                            ? 'left'
                                            : 'center',
                                        },
                                      })}
                                    />
                                  )
                                ) : key.includes(
                                    SessionType.COMMUNITY_WITH_POSTS,
                                  ) ? (
                                  (i as SpotlightCommunityWithPostsSession)
                                    .typeEmbed ===
                                  SpotlightCommunityWithPostEmbedType.MINIGAME ? (
                                    <EmbedPost
                                      {...(i as SpotlightCommunityWithPostsSession)}
                                      applyH1={
                                        (i as SpotlightCommunityWithPostsSession)
                                          .sessionId === heading
                                      }
                                    />
                                  ) : (
                                    <EmbedCommunityWithPosts
                                      {...(i as SpotlightCommunityWithPostsSession)}
                                      applyH1={
                                        (i as SpotlightCommunityWithPostsSession)
                                          .sessionId === heading
                                      }
                                    />
                                  )
                                ) : key.includes(
                                    SessionType.ECOMMERCE_PRODUCS,
                                  ) ? (
                                  <CarePathProductWidgets
                                    title={
                                      (i as SpotlightEcommerceProductsSession)
                                        .title
                                    }
                                    products={
                                      (i as SpotlightEcommerceProductsSession)
                                        .products
                                    }
                                    bgColor={activeTheme.primary}
                                    applyH1={
                                      (i as SpotlightEcommerceProductsSession)
                                        .sessionId === heading
                                    }
                                  />
                                ) : key.includes(SessionType.LEADGEN) ? (
                                  <>
                                    <LeadgenWithBanner
                                      ref={(el) => {
                                        if (
                                          (i as SpotlightLeadgenSession)
                                            .isModal &&
                                          idx === 0
                                        )
                                          leadgenRef.current[i.idScroll] = el
                                      }}
                                      autoOpen={!isSoya}
                                      scrollable={!isSoya}
                                      isModal={
                                        (i as SpotlightLeadgenSession).isModal
                                      }
                                      desktopModalType={
                                        isSoya ? 'fullscreen' : 'normal'
                                      }
                                      data={i as SpotlightLeadgenSession}
                                      primaryColor={activeTheme.primary}
                                      styles={leadgenStyles}
                                      onModalChange={handleStopTrackingIdle}
                                    />
                                    {isSoya && (
                                      <LeadgenWithBanner
                                        data={i as SpotlightLeadgenSession}
                                        isModal
                                        scrollable
                                        desktopModalType={
                                          isSoya ? 'fullscreen' : 'normal'
                                        }
                                        modalBanner={(() => {
                                          const imageBanner = spotlight.listSessions
                                            .filter(
                                              (i) =>
                                                i.sessionLayout ===
                                                SessionType.HERO_BANNER_FULLWIDTH,
                                            )
                                            .slice(
                                              -1,
                                            )?.[0] as SpotlightHeroBannerSession

                                          return imageBanner ? (
                                            <HeroBanner
                                              colorArr={[]}
                                              keepRatio={false}
                                              layout={CustomBanner}
                                              sessionProps={imageBanner}
                                              mappingIconList={mappingIconList}
                                              bannerTracking={{
                                                'data-event-action':
                                                  'Dynamic Banner Click',
                                              }}
                                            />
                                          ) : undefined
                                        })()}
                                        modalProps={{
                                          zIndex: 100,
                                        }}
                                        hashToOpenModal={
                                          SOYA_HASH_TO_OPEN_LEAD_MODAl
                                        }
                                        primaryColor={activeTheme.primary}
                                        onDone={() => {
                                          push(
                                            {
                                              pathname,
                                              query,
                                              hash: '',
                                            },
                                            undefined,
                                            {
                                              scroll: false,
                                            },
                                          )
                                          chatbotRef?.current?.resetChatId()
                                        }}
                                        styles={leadgenStyles}
                                      />
                                    )}
                                  </>
                                ) : key.includes(
                                    SessionType.HERO_BANNER_DEFAULT,
                                  ) ||
                                  key.includes(
                                    SessionType.HERO_BANNER_FULLWIDTH,
                                  ) ? (
                                  <HeroBanner
                                    {...commonBannerSession}
                                    keepRatio={false}
                                    onBannerLoad={() =>
                                      scrollToHash.current &&
                                      handleScrollToHash({
                                        idScroll: i.idScroll,
                                        loaded: true,
                                      })
                                    }
                                    layout={
                                      key.includes(
                                        SessionType.HERO_BANNER_FULLWIDTH,
                                      ) ||
                                      key.includes(SessionType.FOOTER_BANNER)
                                        ? CustomBanner
                                        : DefaultBanner
                                    }
                                    sessionProps={
                                      i as SpotlightHeroBannerSession
                                    }
                                    bannerTracking={{
                                      'data-event-action':
                                        'Dynamic Banner Click',
                                    }}
                                    data-auto-slide={
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming
                                    }
                                    autoSlide={Boolean(
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming,
                                    )}
                                    delayTime={
                                      (i as SpotlightHeroBannerSession)
                                        .autoSlideTiming
                                    }
                                  />
                                ) : key.includes(SessionType.HEALTH_TOOL) ? (
                                  <HealthToolEmbed
                                    data={i as SpotlightHealthToolSession}
                                    applyH1={
                                      (i as SpotlightHealthToolSession)
                                        .sessionId === heading
                                    }
                                  />
                                ) : key.includes(SessionType.CONTENT_BANNER) ? (
                                  <AppIntroCards
                                    data={
                                      (i as ContentBannerSession).listBanner
                                    }
                                    className="wide-container"
                                    isSingleLayout={
                                      (i as ContentBannerSession).singleLayout
                                    }
                                    data-vertical-layout={
                                      !(i as ContentBannerSession).singleLayout
                                    }
                                  />
                                ) : key.includes(
                                    SessionType.HEALTH_TOOL_LIST,
                                  ) ? (
                                  <InfoCardWithSmallIconSection
                                    title={
                                      (i as SpotlightHealthToolListSession)
                                        .title
                                    }
                                    description={
                                      (i as SpotlightHealthToolListSession)
                                        .description
                                    }
                                    data={
                                      (i as SpotlightHealthToolListSession)
                                        .healthTools
                                    }
                                    innerClass="wide-container"
                                  />
                                ) : key.includes(SessionType.VISION_SECTION) ? (
                                  <InfoGardientSection
                                    heading={
                                      (i as SpotlightVisionSession).title
                                    }
                                    description={
                                      (i as SpotlightVisionSession).description
                                    }
                                    image={(i as SpotlightVisionSession).image}
                                    innerClass="narrow-container"
                                  />
                                ) : key.includes(SessionType.FOOTER_BANNER) ? (
                                  <div className="wide-container">
                                    <AppFooterBanner
                                      banner={{
                                        desktop: (i as SpotlightHeroBannerSession)
                                          .imageBanner?.[0]?.desktopBanner,
                                        mobile: (i as SpotlightHeroBannerSession)
                                          .imageBanner?.[0]?.mobileBanner,
                                      }}
                                      url={
                                        (i as SpotlightHeroBannerSession)
                                          .imageBanner?.[0]?.linkTo
                                      }
                                    />
                                  </div>
                                ) : key.includes(SessionType.QUOTATION) ? (
                                  <Quotations
                                    color={activeTheme.primary}
                                    wrapperProps={
                                      (i as SpotlightQuotationsSession)
                                        .wrapperProps
                                    }
                                    // innerClass={'narrow-container'}
                                    data={
                                      (i as SpotlightQuotationsSession)
                                        .quotations
                                    }
                                  />
                                ) : key.includes(
                                    SessionType.SCHEDULED_EVENT,
                                  ) ? (
                                  <ScheduleEvent
                                    {...(i as SpotlightScheduleEventSession)}
                                    applyH1={
                                      (i as SpotlightScheduleEventSession)
                                        .sessionId === heading
                                    }
                                  />
                                ) : key.includes(SessionType.CONTENT_EXPERT) ? (
                                  <ContentExpert
                                    {...(i as SpotlightContentExpertSession)}
                                    applyH1={
                                      (i as SpotlightContentExpertSession)
                                        .sessionId === heading
                                    }
                                    wrapperPropsStyles={{
                                      styles: {
                                        ...(isVinamilk && {
                                          header: {
                                            '.title': {
                                              color: '#001AA9',
                                            },
                                            '.description': {
                                              color: '#595959',
                                            },
                                          },
                                        }),
                                      },
                                    }}
                                  />
                                ) : key.includes(
                                    SessionType.CUSTOMIZED_BLOCK,
                                  ) ? (
                                  <CustomizedBlock
                                    data={i as SpotlightCustomizeBlockSession}
                                  />
                                ) : key.includes(SessionType.CTA_BANNER) ? (
                                  <CtaBannerSection
                                    data={i as SpotlightCTABannerSession}
                                  />
                                ) : null}
                              </Box>
                            )
                          })}
                        </Box>
                      )
                    },
                  )
                })()}
                {isPfizer && (
                  <Container>
                    <Box
                      sx={(theme) => ({
                        padding: '0 0 40px',
                        [theme.fn.largerThan('sm')]: {
                          padding: '20px 0 80px',
                        },
                      })}
                    >
                      <PfizerDisclaimer />
                    </Box>
                  </Container>
                )}
              </ScrollingBlocks>
            </Wrapper>
          </StyledLandingWrapper>
          {!spotlight?.disableAd && (
            <AdBlockContainer>
              <FixedBottomAdBlock
                className="carepath-bottom-ad"
                adUnitPath={adUnitPath}
                isMobile={isMobile}
                customTargetings={customTargetings}
              />
              <OutOfPageAd id={`div-gpt-out-of-page`} adUnitPath={adUnitPath} />
            </AdBlockContainer>
          )}
          {listRiskscreener.length > 0 && (
            <RiskScreenerModal
              ref={riskScreenerModalRef}
              onOpen={() => handleStopTrackingIdle(true)}
              isTypeForm={Boolean(typeFormObj.mobileFormId)}
            >
              <RiskScreenerEmbed
                data={{
                  ...(listRiskscreener?.[0] as SpotlightRiskScreenerSession),
                  backgroundColor:
                    getSpecifySessionBgByPath(isRoche)?.[
                      SessionType.EMBEDDING_RISKSCREENER
                    ] || listRiskscreener?.[0].backgroundColor,
                }}
                isDisabledAnimationOnMobile
                handleOnCloseMobile={() =>
                  riskScreenerModalRef?.current?.close()
                }
                primaryColor={activeTheme.primary}
                typeoformId={typeFormObj.mobileFormId}
              />
            </RiskScreenerModal>
          )}
          {isRoche && <HpvBanner />}

          {spotlight.intensivePopup?.leavingPopup?.title &&
            spotlight.intensivePopup?.leavingPopup?.message && (
              <ForwardConfirmLeavePage
                ref={confirmLeavePageRef}
                backgroundColor={
                  spotlight.intensivePopup.leavingPopup.backgroundColor
                }
                buttonColor={spotlight.intensivePopup.leavingPopup.buttonColor}
                titleColor={spotlight.intensivePopup.leavingPopup.titleColor}
                desktopImage={
                  spotlight.intensivePopup.leavingPopup.desktopImage
                }
                mobileImage={spotlight.intensivePopup.leavingPopup.mobileImage}
                title={spotlight.intensivePopup.leavingPopup.title}
                message={spotlight.intensivePopup.leavingPopup.message}
                messageColor={
                  spotlight.intensivePopup.leavingPopup.messageColor
                }
                defaultSection={{
                  hash: navigating?.[0]?.idScroll,
                  cta: navigating?.[0]?.title,
                  btnTracking: {
                    dataEventCategory: 'Service Connection',
                    dataEventAction: `${
                      DATA_TRACKING_ACTION_BY_SESSION[
                        navigating?.[0]?.sessionLayout
                      ] || ''
                    } - Intent popup`,
                    dataEventLabel: `${currentUrl.split('#')[0]}#${decodeURI(
                      navigating?.[0]?.idScroll,
                    )}`,
                  },
                }}
                onItemClick={onItemClick}
                onChange={handleStopTrackingIdle}
              />
            )}
          <ForwardIdlePopup
            ref={idlePopupRef}
            title={spotlight?.intensivePopup?.idlePopup?.title}
            backgroundColor={
              spotlight?.intensivePopup?.idlePopup?.backgroundColor
            }
            buttonColor={spotlight?.intensivePopup?.idlePopup?.buttonColor}
            titleColor={spotlight?.intensivePopup?.idlePopup?.titleColor}
            desktopImage={spotlight?.intensivePopup?.idlePopup?.desktopImage}
            mobileImage={spotlight?.intensivePopup?.idlePopup?.mobileImage}
            onItemClick={onItemClick}
            listItems={navigating.slice(0, 2).map((i) => ({
              title: i.title,
              hash: i.idScroll,
              btnTracking: {
                dataEventCategory: 'Service Connection',
                dataEventAction: `${
                  DATA_TRACKING_ACTION_BY_SESSION[i.sessionLayout] || ''
                } - Idle popup`,
                dataEventLabel: `${currentUrl.split('#')[0]}#${i.idScroll}`,
              },
            }))}
          />
          {hasIntroCarepathModal &&
            query.external === '1' &&
            listClickable.length > 0 && (
              <IntroCarePathModal
                onItemClick={onItemClick}
                hashToShow={
                  listClickable?.[0]?.idScroll ||
                  listClickable?.[0]?.hashtag ||
                  ''
                }
              />
            )}
          {isSoya && process.env.NEXT_PUBLIC_ENABLE_AI_CHAT === 'true' && (
            <AIChatBot
              ref={chatbotRef}
              name="Hello Sehat Expert"
              avatar={CommonGAssets.getAssetPath('soya/ai_avatar.png')}
              starterAvatarSize={128}
              showHighlightMessage
              cookieId={SOYA_AI_CHAT_ID}
              isShortVer={isMobile}
              widgetModalProps={{
                zIndex: 99,
              }}
              onClickHighlightMess={() =>
                push(
                  {
                    pathname,
                    query,
                    hash: SOYA_HASH_TO_OPEN_LEAD_MODAl,
                  },
                  undefined,
                  {
                    shallow: true,
                    scroll: false,
                  },
                )
              }
            />
          )}
        </ArticleStoreProvider>
      </NavigatorLayout>
    </CarePathDefaultContext.Provider>
  )
}

export { CarePathDefaultTemplate }
